/**
 * Slide-overs de Lançamentos (mockup Fase 5 V2.2): Novo lançamento e
 * Importar OFX. Escopo .fin2-panel. Reusam os services existentes
 * (criarLancamento, importarOfx, listarContas/Categorias/Centros) — nenhuma
 * mudança de lógica/dados; só a camada visual.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiSearch, FiUploadCloud } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarLancamento, criarLancamento, importarOfx, listarCategorias, listarCentros,
  listarContas,
} from "../../../services/financeiro";
import { listarPedidos } from "../../../services/pedidos";
import { InlineError } from "../../Crm/styles";


function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}


/**
 * Painel de lancamento — cria ou EDITA.
 *
 * M-TRA-8 (auditoria 2026-08-07): o backend sempre aceitou PATCH e o
 * `atualizarLancamento` ja existia no services do frontend, mas nenhuma
 * tela chamava. Errar a categoria ou a data de um lancamento significava
 * conviver com o erro — e a DRE e o Fluxo de Caixa saem exatamente desses
 * dois campos.
 *
 * Em vez de um segundo formulario, o mesmo painel recebe `lancamento`:
 * um formulario so, uma validacao so.
 */
export function NovoLancamentoPanel({ open, onClose, onSaved, tipoInicial = "ENTRADA", lancamento = null }) {
  const editando = lancamento != null;
  const { request } = useAuth();
  const [tipo, setTipo] = useState(tipoInicial);
  const [valor, setValor] = useState("");
  const [contaId, setContaId] = useState("");
  const [categoriaId, setCategoriaId] = useState("");
  const [centroId, setCentroId] = useState("");
  const [descricao, setDescricao] = useState("");
  const [dataCompetencia, setDataCompetencia] = useState(hojeISO());
  const [jaPago, setJaPago] = useState(false);
  const [dataCaixa, setDataCaixa] = useState(hojeISO());
  const [erro, setErro] = useState(null);

  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [centros, setCentros] = useState([]);

  // Busca de obra/pedido
  const [pedidoBusca, setPedidoBusca] = useState("");
  const [pedidoSel, setPedidoSel] = useState(null);
  const [pedidoResultados, setPedidoResultados] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (lancamento) {
      setTipo(lancamento.tipo || tipoInicial);
      setValor(String(lancamento.valor ?? "").replace(".", ","));
      setContaId(lancamento.conta || lancamento.conta_id || "");
      setCategoriaId(lancamento.categoria || lancamento.categoria_id || "");
      setCentroId(lancamento.centro_custo || lancamento.centro_custo_id || "");
      setDescricao(lancamento.descricao || "");
      setDataCompetencia(lancamento.data_competencia || hojeISO());
      setJaPago(!!lancamento.data_caixa);
      setDataCaixa(lancamento.data_caixa || hojeISO());
      setErro(null);
      setPedidoBusca(""); setPedidoResultados([]);
      setPedidoSel(
        lancamento.pedido
          ? { id: lancamento.pedido, numero: lancamento.pedido_numero || "" }
          : null,
      );
    } else {
      setTipo(tipoInicial); setValor(""); setContaId(""); setCategoriaId("");
      setCentroId(""); setDescricao(""); setDataCompetencia(hojeISO());
      setJaPago(false); setDataCaixa(hojeISO()); setErro(null);
      setPedidoBusca(""); setPedidoSel(null); setPedidoResultados([]);
    }
    Promise.all([
      listarContas(request, { ativo: true }),
      listarCategorias(request, { ativo: true }),
      listarCentros(request, { ativo: true }),
    ]).then(([c, cat, cen]) => {
      setContas(Array.isArray(c) ? c : c?.results || []);
      setCategorias(Array.isArray(cat) ? cat : cat?.results || []);
      setCentros(Array.isArray(cen) ? cen : cen?.results || []);
    }).catch(() => {});
  }, [open, request, tipoInicial, lancamento]);

  // Categoria filtrada pelo tipo (RECEITA ↔ ENTRADA, DESPESA ↔ SAIDA)
  const categoriasFiltradas = useMemo(() => {
    const alvo = tipo === "ENTRADA" ? "RECEITA" : "DESPESA";
    return categorias.filter((c) => c.tipo === alvo);
  }, [categorias, tipo]);

  // limpa categoria se trocar o tipo e a selecionada não couber
  useEffect(() => {
    if (categoriaId && !categoriasFiltradas.some((c) => c.id === categoriaId)) {
      setCategoriaId("");
    }
  }, [categoriaId, categoriasFiltradas]);

  // Busca de pedido com debounce
  useEffect(() => {
    const termo = pedidoBusca.trim();
    if (termo.length < 2 || pedidoSel) { setPedidoResultados([]); return undefined; }
    let ativo = true;
    const id = setTimeout(() => {
      listarPedidos(request, { busca: termo, pageSize: 6 })
        .then((p) => ativo && setPedidoResultados(Array.isArray(p) ? p : p?.results || []))
        .catch(() => ativo && setPedidoResultados([]));
    }, 300);
    return () => { ativo = false; clearTimeout(id); };
  }, [pedidoBusca, pedidoSel, request]);

  const salvar = useCallback(async () => {
    setErro(null);
    if (!valor || Number(String(valor).replace(/\./g, "").replace(",", ".")) <= 0) {
      setErro("Informe um valor positivo."); throw new Error("valor");
    }
    if (!contaId) { setErro("Selecione a conta."); throw new Error("conta"); }
    if (!categoriaId) { setErro("Selecione a categoria."); throw new Error("categoria"); }
    if (!descricao.trim()) { setErro("Descrição obrigatória."); throw new Error("descricao"); }
    const valorNum = String(valor).replace(/\./g, "").replace(",", ".");
    const dados = {
      conta_id: contaId,
      categoria_id: categoriaId,
      centro_custo_id: centroId || null,
      pedido_id: pedidoSel?.id || null,
      descricao: descricao.trim(),
      valor: valorNum,
      tipo,
      data_competencia: dataCompetencia,
      data_caixa: jaPago ? dataCaixa : null,
      confirmado: jaPago,
    };
    if (editando) {
      await atualizarLancamento(request, lancamento.id, dados);
    } else {
      await criarLancamento(request, { ...dados, observacoes: "" });
    }
    await onSaved?.();
    onClose();
  }, [request, valor, contaId, categoriaId, centroId, pedidoSel, descricao,
      tipo, dataCompetencia, jaPago, dataCaixa, onSaved, onClose,
      editando, lancamento]);

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={editando ? `Editar ${lancamento.numero || "lançamento"}` : "Novo lançamento"}
      subtitle="Competência alimenta a DRE · caixa alimenta o Fluxo"
      width="min(95vw, 520px)"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <AsyncButton onClick={salvar} successMessage={editando ? "Lançamento atualizado." : "Lançamento criado."} silentError>
            Salvar lançamento
          </AsyncButton>
        </>
      }
    >
      <div className="fin2-panel">
        <div className="so-section-label">Tipo</div>
        <div className="seg-radio" role="radiogroup" aria-label="Tipo" style={{ marginBottom: 18 }}>
          <label className={tipo === "ENTRADA" ? "is-on" : ""} onClick={() => setTipo("ENTRADA")}>
            <input type="radio" className="sr-radio" checked={tipo === "ENTRADA"} readOnly /> Entrada
          </label>
          <label className={tipo === "SAIDA" ? "is-on" : ""} onClick={() => setTipo("SAIDA")}>
            <input type="radio" className="sr-radio" checked={tipo === "SAIDA"} readOnly /> Saída
          </label>
        </div>

        <div className="input-group" style={{ marginBottom: 14 }}>
          <label className="input-label">Valor</label>
          <div className="input-prefixed">
            <span style={{ color: "var(--ink-3)", fontWeight: 600 }}>R$</span>
            <input value={valor} onChange={(e) => setValor(e.target.value)}
                   inputMode="decimal" placeholder="0,00" aria-label="Valor" autoFocus />
          </div>
        </div>

        <div className="field-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div className="input-group">
            <label className="input-label">Conta</label>
            <select className="select" value={contaId} onChange={(e) => setContaId(e.target.value)}>
              <option value="">Selecione…</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Categoria <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>· filtrada pelo tipo</span></label>
            <select className="select" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
              <option value="">Selecione…</option>
              {categoriasFiltradas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Centro de custo</label>
            <select className="select" value={centroId} onChange={(e) => setCentroId(e.target.value)}>
              <option value="">— nenhum —</option>
              {centros.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Nº obra / pedido</label>
            {pedidoSel ? (
              <div className="input-prefixed">
                <span className="mono" style={{ flex: 1 }}>{pedidoSel.numero}</span>
                <button type="button" className="btn btn--ghost btn--sm"
                        onClick={() => { setPedidoSel(null); setPedidoBusca(""); }}>×</button>
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <div className="input-prefixed">
                  <FiSearch size={14} aria-hidden="true" />
                  <input value={pedidoBusca} onChange={(e) => setPedidoBusca(e.target.value)}
                         placeholder="buscar pedido…" aria-label="Nº obra ou pedido" />
                </div>
                {pedidoResultados.length > 0 && (
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, marginTop: 4, background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-2)", overflow: "hidden" }}>
                    {pedidoResultados.map((p) => (
                      <button key={p.id} type="button"
                              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", border: 0, borderBottom: "1px solid var(--divider)", cursor: "pointer", fontSize: 13 }}
                              onClick={() => { setPedidoSel(p); setPedidoResultados([]); }}>
                        <strong className="mono">{p.numero}</strong>
                        <span className="muted"> · {p.cliente_nome || p.cliente || ""}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="input-group" style={{ marginBottom: 14 }}>
          <label className="input-label">Descrição</label>
          <input className="input" value={descricao} onChange={(e) => setDescricao(e.target.value)}
                 maxLength={300} placeholder="Ex.: Joaquim Castro · parcela 2/3" />
        </div>

        <div className="so-section-label">Regime híbrido</div>
        <div className="input-group" style={{ marginBottom: 12 }}>
          <label className="input-label">Data de competência <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>· alimenta a DRE</span></label>
          <input className="input" type="date" value={dataCompetencia}
                 onChange={(e) => setDataCompetencia(e.target.value)} />
        </div>
        <label className="flex items-center gap-2" style={{ fontSize: 13, padding: "11px 12px", background: "var(--surface-2)", borderRadius: "var(--radius-md)", cursor: "pointer", marginBottom: 12 }}>
          <input type="checkbox" checked={jaPago} onChange={(e) => setJaPago(e.target.checked)}
                 style={{ accentColor: "var(--accent)" }} /> Já pago / recebido?
        </label>
        {jaPago && (
          <div className="input-group">
            <label className="input-label">Data de caixa <span style={{ color: "var(--ink-4)", fontWeight: 400 }}>· alimenta o Fluxo</span></label>
            <input className="input" type="date" value={dataCaixa}
                   onChange={(e) => setDataCaixa(e.target.value)} />
          </div>
        )}

        <div style={{ padding: "12px 14px", background: "var(--info-bg)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--info-text)", lineHeight: 1.5, marginTop: 14 }}>
          <strong>Competência ≠ caixa.</strong> Um lançamento de competência sem data de
          caixa fica "a receber/pagar" no Fluxo até a baixa.
        </div>

        {erro && <InlineError style={{ marginTop: 12 }}>{erro}</InlineError>}
      </div>
    </SlideOverPanel>
  );
}


export function ImportarOfxPanel({ open, onClose, onImported }) {
  const { request } = useAuth();
  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [contaId, setContaId] = useState("");
  const [catReceita, setCatReceita] = useState("");
  const [catDespesa, setCatDespesa] = useState("");
  const [arquivo, setArquivo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setContaId(""); setCatReceita(""); setCatDespesa("");
    setArquivo(null); setResultado(null); setErro(null);
    Promise.all([
      listarContas(request, { ativo: true }),
      listarCategorias(request, { ativo: true }),
    ]).then(([c, cat]) => {
      setContas(Array.isArray(c) ? c : c?.results || []);
      setCategorias(Array.isArray(cat) ? cat : cat?.results || []);
    }).catch(() => {});
  }, [open, request]);

  const receitas = categorias.filter((c) => c.tipo === "RECEITA");
  const despesas = categorias.filter((c) => c.tipo === "DESPESA");

  const importar = useCallback(async () => {
    setErro(null);
    if (!contaId) { setErro("Selecione a conta destino."); throw new Error("conta"); }
    if (!arquivo) { setErro("Escolha o arquivo .ofx."); throw new Error("arquivo"); }
    const r = await importarOfx(request, {
      conta_id: contaId, arquivo,
      categoria_receita_default_id: catReceita || undefined,
      categoria_despesa_default_id: catDespesa || undefined,
    });
    setResultado(r);
    await onImported?.();
  }, [request, contaId, arquivo, catReceita, catDespesa, onImported]);

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Importar extrato OFX"
      subtitle="Alimenta a fila de conciliação"
      width="min(95vw, 520px)"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>Fechar</button>
          <AsyncButton onClick={importar} successMessage="Importação concluída." silentError
                       disabled={!arquivo || !contaId}>
            <FiUploadCloud size={14} aria-hidden="true" /> Importar
          </AsyncButton>
        </>
      }
    >
      <div className="fin2-panel">
        <button type="button" className="ofx-drop" onClick={() => fileRef.current?.click()}>
          <FiUploadCloud size={26} aria-hidden="true" />
          <div className="ofx-drop__t">{arquivo ? arquivo.name : "Clique para selecionar o arquivo .ofx"}</div>
          <div className="ofx-drop__s">Itaú, Bradesco, Caixa</div>
        </button>
        <input ref={fileRef} type="file" accept=".ofx" style={{ display: "none" }}
               onChange={(e) => { setArquivo(e.target.files?.[0] || null); setResultado(null); }} />

        <div className="field-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
          <div className="input-group">
            <label className="input-label">Conta destino</label>
            <select className="select" value={contaId} onChange={(e) => setContaId(e.target.value)}>
              <option value="">Selecione…</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Categoria padrão (entradas)</label>
            <select className="select" value={catReceita} onChange={(e) => setCatReceita(e.target.value)}>
              <option value="">— opcional —</option>
              {receitas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Categoria padrão (saídas)</label>
            <select className="select" value={catDespesa} onChange={(e) => setCatDespesa(e.target.value)}>
              <option value="">— opcional —</option>
              {despesas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
        </div>

        {resultado && (
          <div style={{ marginTop: 18 }}>
            <div className="so-section-label">Resultado da importação</div>
            <div className="ofx-stats">
              <div className="ofx-stat"><div className="n" style={{ color: "var(--success-text)" }}>{resultado.criados ?? 0}</div><div className="k">Criados</div></div>
              <div className="ofx-stat"><div className="n" style={{ color: "var(--ink-3)" }}>{resultado.duplicados_ignorados ?? resultado.ignorados_duplicados ?? 0}</div><div className="k">Duplicados ignorados</div></div>
              <div className="ofx-stat"><div className="n" style={{ color: "var(--danger-text)" }}>{resultado.erros ?? 0}</div><div className="k">Erros</div></div>
            </div>
          </div>
        )}

        {erro && <InlineError style={{ marginTop: 12 }}>{erro}</InlineError>}
      </div>
    </SlideOverPanel>
  );
}
